/**
 * The variable path an `input.images` upload occupies. Renaming is keyed off
 * this rather than off the operation name: it is the field's shape that makes
 * the part name meaningful, and scoping this way covers every mutation whose
 * input carries `images` without an allowlist to keep in step.
 */
const IMAGES_VARIABLE_PATH_PATTERN = /^variables\.input\.images\.\d+$/;

const isImagesUpload = (paths: unknown): paths is string[] =>
  Array.isArray(paths) &&
  paths.length > 0 &&
  paths.every(
    (path) =>
      typeof path === 'string' && IMAGES_VARIABLE_PATH_PATTERN.test(path),
  );

/**
 * The `imageRef` behind a file name: everything before the last dot.
 *
 * The two are not interchangeable. The part's `filename` must carry an
 * extension or the upload is not recognised as a file, while the field name
 * must be the bare `imageRef` the backend correlates on — so the extension is
 * added when the `File` is built (`convertDataUrlToFile`) and taken back off
 * here. A name with no dot is returned unchanged.
 */
const removeFileNameExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf('.');

  return lastDotIndex === -1 ? fileName : fileName.slice(0, lastDotIndex);
};

/**
 * Renames every `input.images` part of a GraphQL multipart body to the
 * `imageRef` of the file it carries — its name with the extension stripped —
 * and rewrites the `map` to match.
 *
 * The backend correlates an uploaded image with its `imageRef` by the part's
 * field name — the `map` key. `apollo-upload-client` numbers those keys `1..n`
 * and appends the `map` before any file, so it cannot know which file lands at
 * which index and offers no option to influence it. Its `fetch` option is the
 * last point at which the assembled body can still be corrected, which is
 * where this runs.
 *
 * The part's own `filename` keeps its extension: dropping it stops the backend
 * from picking the part up as a file at all. Field name without, filename with
 * — the shape `cropink-figma-plugin` sends successfully (`GraphQL.service.ts`,
 * `removeFileNameExtension(file.name)` as the `map` key over a `File` named
 * `${imageRef}.png`).
 *
 * Only `input.images` parts are touched. A logo, an asset image or a font is
 * addressed by its own input field and carries whatever name the user's file
 * had — renaming those would be meaningless, and two identically named picks
 * would collide.
 *
 * Never throws: a body this does not recognise is returned as-is, which sends
 * exactly what `apollo-upload-client` built.
 */
export const remapImageRefUploadKeys = (form: FormData): FormData => {
  const rawMap = form.get('map');
  const operations = form.get('operations');

  if (typeof rawMap !== 'string' || operations === null) return form;

  let parsedMap: unknown;

  try {
    parsedMap = JSON.parse(rawMap);
  } catch (error) {
    console.error(error);

    return form;
  }

  // The `map` part must decode to a plain object of `key -> paths` entries.
  // `JSON.parse` happily succeeds on `null`, a bare string/number, or an
  // array — none of those are iterable as map entries, so treat them the
  // same as unparseable JSON rather than let `Object.entries` throw.
  if (
    typeof parsedMap !== 'object' ||
    parsedMap === null ||
    Array.isArray(parsedMap)
  ) {
    return form;
  }

  const map = parsedMap as Record<string, unknown>;
  const nextMap: Record<string, unknown> = {};
  const nextKeys: [originalKey: string, nextKey: string][] = [];

  Object.entries(map).forEach(([key, paths]) => {
    const value = form.get(key);

    const ref =
      value instanceof File ? removeFileNameExtension(value.name) : '';

    // A ref already claimed by an earlier part keeps its owner: collapsing
    // two parts onto one key would drop one file's bytes with no error.
    const nextKey =
      isImagesUpload(paths) && ref && !(ref in nextMap) ? ref : key;

    nextMap[nextKey] = paths;
    nextKeys.push([key, nextKey]);
  });

  const next = new FormData();

  next.append('operations', operations);
  next.append('map', JSON.stringify(nextMap));

  nextKeys.forEach(([key, nextKey]) => {
    const value = form.get(key);

    if (value === null) return;

    if (value instanceof File) next.append(nextKey, value, value.name);
    else next.append(nextKey, value);
  });

  return next;
};
